import React, { useRef, useState } from "react";
import { Film, X, Images, MessageSquare, Upload, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../lib/api";

const fileUrl = (path) => mediaUrl(`/files/${path}`, "");
import { GalleryPicker } from "./GalleryPicker";
import { ChatFilesPicker } from "./ChatFilesPicker";

// Do the attached references fit a video model's provider caps (counts only — sizes/durations are checked on run)?
export const fitsRefs = (o, refs) => {
  const lim = o?.ref_limits || {};
  const img = refs.filter((r) => r.kind === "image").length, vid = refs.filter((r) => r.kind === "video").length;
  return img <= (lim.image_count ?? 0) && vid <= (lim.video_count ?? 0);
};

export const refVideoSeconds = (refs) => refs.filter((r) => r.kind === "video").reduce((a, r) => a + (Number(r.duration) || 0), 0);

const fmt = (n) => Number(n).toLocaleString("id-ID", { maximumFractionDigits: 1 });
export const limitText = (o) => {
  const lim = o?.ref_limits;
  if (!lim) return "";
  if (!lim.supported) return lim.image_count ? `${o.label}: 1 gambar sebagai frame awal (≤${lim.image_mb} MB), tanpa video` : `${o.label}: tidak mendukung referensi`;
  const sec = lim.video_sec ? `, ${fmt(lim.video_sec[0])}–${fmt(lim.video_sec[1])} dtk, total ≤${fmt(lim.video_total_sec)} dtk` : "";
  return `${o.label}: maks. ${lim.image_count} gambar (≤${lim.image_mb} MB) & ${lim.video_count} video (≤${lim.video_mb} MB${sec})`;
};

// Video length from the browser (the server probes the file when this fails).
const probeDuration = (url) => new Promise((resolve) => {
  const v = document.createElement("video"); v.preload = "metadata";
  const done = (d) => { v.removeAttribute("src"); resolve(d && isFinite(d) ? Math.round(d * 100) / 100 : undefined); };
  const t = setTimeout(() => done(), 8000);
  v.onloadedmetadata = () => { clearTimeout(t); done(v.duration); };
  v.onerror = () => { clearTimeout(t); done(); };
  v.src = url;
});

// Reference images/videos on a pending video card — same sources as a Workspace stage: upload, Galeri, files sent in chat.
export function VideoReferences({ m, cid, refs, setRefs, model, dark }) {
  const [picker, setPicker] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const lim = model?.ref_limits || { image_count: 30, image_mb: 30, video_count: 10, video_mb: 200 };
  const save = async (next) => {
    try { const r = await api.put(`/conversations/${cid}/messages/${m.id}/video-references`, { references: next }); setRefs(r.data.references); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan referensi"); }
  };
  const add = async (items) => {
    const picked = items.filter((it) => ["image", "video"].includes(it.kind) && it.path && !refs.find((r) => r.path === it.path));
    if (!picked.length) return;
    setBusy("add");
    try {
      const withDur = await Promise.all(picked.map(async (it) => ({ path: it.path, kind: it.kind, name: it.name, size: it.size, duration: it.kind === "video" ? await probeDuration(fileUrl(it.path)) : undefined })));
      await save([...refs, ...withDur]);
    } finally { setBusy(""); }
  };
  const upload = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy("upload");
    try {
      const out = [];
      for (const f of files) {
        const isVideo = f.type.startsWith("video/");
        const cap = isVideo ? lim.video_mb : lim.image_mb;
        if (cap && f.size > cap * 1024 * 1024) { toast.error(`${f.name}: maksimal ${cap} MB untuk ${model?.label || "model ini"}`); continue; }
        const fd = new FormData(); fd.append("file", f);
        try { const r = await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); out.push(r.data); }
        catch (err) { toast.error(err?.response?.data?.detail || `Gagal mengunggah ${f.name}`); }
      }
      if (out.length) await add(out);
    } finally { setBusy(""); }
  };
  const full = refs.filter((r) => r.kind === "image").length >= lim.image_count && refs.filter((r) => r.kind === "video").length >= lim.video_count;
  const btn = `flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold transition disabled:opacity-50 ${dark ? "bg-white/10 hover:bg-white/15" : "bg-white hover:bg-slate-100 shadow-sm"}`;
  return (
    <div data-testid="video-references">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`mr-1 ${dark ? "text-white/60" : "text-[#2F6BFF]"}`}>Referensi</span>
        {refs.map((r) => (
          <span key={r.path} className={`flex items-center gap-1 rounded-lg py-0.5 pl-0.5 pr-1.5 text-[11px] ${dark ? "bg-white/10" : "bg-white shadow-sm"}`} data-testid="video-ref-item" data-no-i18n="true">
            {r.kind === "image" ? <img src={fileUrl(r.path)} alt="" className="h-6 w-6 rounded object-cover" /> : <Film size={14} className="mx-1" />}
            <span className="max-w-[110px] truncate">{r.name}</span>
            {r.kind === "video" && r.duration ? <span className="opacity-60">{fmt(r.duration)} dtk</span> : null}
            <button type="button" onClick={() => save(refs.filter((x) => x.path !== r.path))} className="opacity-60 hover:opacity-100" data-testid="video-ref-remove"><X size={11} /></button>
          </span>
        ))}
        <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" multiple className="hidden" onChange={upload} data-testid="video-ref-file-input" />
        <button type="button" disabled={!!busy || full} onClick={() => fileRef.current?.click()} className={btn} data-testid="video-ref-upload">{busy === "upload" ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Unggah</button>
        <button type="button" disabled={!!busy || full} onClick={() => setPicker("gallery")} className={btn} data-testid="video-ref-add">{busy === "add" ? <Loader2 size={11} className="animate-spin" /> : <Images size={11} />} Galeri</button>
        <button type="button" disabled={!!busy || full} onClick={() => setPicker("chat")} className={btn} data-testid="video-ref-chat"><MessageSquare size={11} /> Dari Chat</button>
      </div>
      <p className={`mt-1 flex items-start gap-1 text-[10px] ${dark ? "text-white/50" : "text-[#2F6BFF]"}`} data-testid="video-ref-limits"><Info size={10} className="mt-0.5 shrink-0" /> {limitText(model) || "Lampirkan gambar/video agar karakter, produk & gaya konsisten."}{model?.billing === "tokens" && refs.some((r) => r.kind === "video") ? " · Durasi video referensi ikut ditagih (tarif ×" + fmt(model.ref_video_factor) + ")." : ""}</p>
      {picker === "gallery" && <GalleryPicker max={20} onClose={() => setPicker("")} onPick={(items) => { setPicker(""); add(items); }} />}
      {picker === "chat" && <ChatFilesPicker max={20} onClose={() => setPicker("")} onPick={(items) => { setPicker(""); add(items); }} />}
    </div>
  );
}
